import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
    Legend
} from 'recharts';
import { Table, Input, Card, Radio, Button, Progress } from 'antd';

import { getUsuarioStorage } from '../../helpers/getUsuarioStorage';
import { useHideMenu } from '../../hooks/useHideMenu';
import { useGetPantallas } from '../../hooks/useGetPantallas';
import { NoAcceso } from '../../components/NoAcceso';
import LoadingOverlay from '../../components/LoadingOverlay';
import { serverApi } from '../../api';
import { TablasResumenDashboard } from './components/TablasResumenDashboard';

const COLORS_SERIES = [
    '#3b82f6', // Azul
    '#10b981', // Verde
    '#f59e0b', // Amarillo
    '#ef4444', // Rojo
    '#8b5cf6', // Morado
    '#ec4899', // Rosa
    '#06b6d4', // Cyan
    '#f97316'  // Naranja
];

const ESTADOS_COLORS = {
    'ESPERA': '#94a3b8',
    'LLAMADO': '#3b82f6',
    'ATENDIENDO': '#eab308',
    'FINALIZADO': '#10b981',
    'NO RESPONDIO': '#ef4444',
    'CANCELADO': '#f97316',
    'PAUSADO': '#a855f7'
};

const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const hoy = formatDate(new Date());

const initialForm = {
    codigoEntidad: '',
    codigoUbicacion: '',
    fechaInicio: hoy,
    fechaFinal: hoy,
    filtroTiempo: 'dia',
    uidTipoAtencion: '',
    uidModulo: '',
    uidAgente: ''
};

export const NuevoDashboard = () => {
    const [usuario] = useState(getUsuarioStorage());
    const pantallas = useGetPantallas();
    const navigate = useNavigate();
    const [query, setQuery] = useState(initialForm);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('ALL');

    const [dashboard, setDashboard] = useState({
        resumen: {
            creados: 0,
            atendidos: 0,
            atendidosConsultorio: 0,
            promedioLlamado: '00:00:00',
            promedioEsperaAtencion: '00:00:00',
            promedioAtencion: '00:00:00'
        },
        porAtencion: [],
        porModulo: [],
        porAgente: [],
        especialistas: [],
        evolucion: [],
        estados: [],
        opciones: {
            tiposAtencion: [],
            modulos: [],
            agentes: []
        }
    });

    useHideMenu(false);

    // Redirección si el usuario no ha iniciado sesión
    useEffect(() => {
        if (usuario.uidUsuario === null) {
            navigate('/Ingreso');
        }
    }, [usuario.uidUsuario, navigate]);

    // Establecer la entidad y ubicación del usuario logueado
    useEffect(() => {
        if (usuario) {
            setQuery(prev => ({
                ...prev,
                codigoEntidad: usuario.codigoEntidad,
                codigoUbicacion: usuario.codigoUbicacion
            }));
        }
    }, [usuario]);

    const cargarDashboard = useCallback(async () => {
        try {
            setIsLoading(true);
            const resp = await serverApi.post('/informes/nuevo-dashboard', query);
            if (resp.data) {
                setDashboard(resp.data);
            }
        } catch (error) {
            console.error('Error al cargar nuevo dashboard:', error);
        } finally {
            setIsLoading(false);
        }
    }, [query]);

    useEffect(() => {
        if (query.codigoEntidad && query.codigoUbicacion) {
            cargarDashboard();
        }
    }, [query, cargarDashboard]);

    /* Presets de Fecha */
    const handlePresetDia = () => {
        const t = formatDate(new Date());
        setQuery(prev => ({ ...prev, fechaInicio: t, fechaFinal: t, filtroTiempo: 'dia' }));
    };

    const handlePresetMes = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setQuery(prev => ({
            ...prev,
            fechaInicio: formatDate(firstDay),
            fechaFinal: formatDate(lastDay),
            filtroTiempo: 'mes'
        }));
    };

    const handlePresetAnio = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), 0, 1);
        const lastDay = new Date(now.getFullYear(), 11, 31);
        setQuery(prev => ({
            ...prev,
            fechaInicio: formatDate(firstDay),
            fechaFinal: formatDate(lastDay),
            filtroTiempo: 'anio'
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setQuery(prev => ({ ...prev, [name]: value }));
    };

    // Exportación a Excel con múltiples hojas estructuradas
    const exportToExcelMultiSheet = () => {
        const wb = XLSX.utils.book_new();

        // 1. Resumen General
        const resumenData = [
            { "Métrica": "Tickets Creados en el Rango", "Valor": dashboard.resumen.creados || 0 },
            { "Métrica": "Tickets Atendidos (General)", "Valor": dashboard.resumen.atendidos || 0 },
            { "Métrica": "Tickets Atendidos (Consultorios)", "Valor": dashboard.resumen.atendidosConsultorio || 0 },
            {
                "Métrica": "Porcentaje Atendidos en Consultorio",
                "Valor": dashboard.resumen.atendidos
                    ? `${((dashboard.resumen.atendidosConsultorio / dashboard.resumen.atendidos) * 100).toFixed(1)}%`
                    : "0%"
            },
            { "Métrica": "T. Promedio Llamado (Sede)", "Valor": dashboard.resumen.promedioLlamado || "00:00:00" },
            { "Métrica": "T. Promedio Espera Inicio Atención", "Valor": dashboard.resumen.promedioEsperaAtencion || "00:00:00" },
            { "Métrica": "T. Promedio Duración Atención", "Valor": dashboard.resumen.promedioAtencion || "00:00:00" }
        ];
        const wsResumen = XLSX.utils.json_to_sheet(resumenData);
        XLSX.utils.book_append_sheet(wb, wsResumen, "Resumen General");

        // 2. Personal (Agentes y Médicos)
        const especialistasData = (dashboard.especialistas || []).map(item => ({
            "Nombre": item.nombreCompleto,
            "Rol": item.tipo === 'MEDICO' ? 'Médico' : 'Agente',
            "Pacientes Atendidos": item.cantidadAtendidos,
            "Total Asignados": item.cantidadAsignados,
            "T. Promedio Llamado (Espera en Cola)": item.promedioLlamado,
            "T. Promedio Espera Atención (Traslado/Llamado)": item.promedioEsperaAtencion,
            "T. Promedio Atención (Servicio)": item.promedioAtencion,
            "No Respondió": item.totalNoRespondio,
            "Cancelado": item.totalCancelado,
            "Promedio de Llamados Realizados": item.promedioLlamadosNum
        }));
        const wsEspecialistas = XLSX.utils.json_to_sheet(especialistasData);
        XLSX.utils.book_append_sheet(wb, wsEspecialistas, "Personal (Agentes y Médicos)");

        // 3. Por Tipo de Atención
        const atencionesData = (dashboard.porAtencion || []).map(item => ({
            "Tipo de Atención": item.servicio || item.tipoAtencion,
            "Cantidad Atendidos": item.cantidad,
            "Prom. Llamado": item.promedioLlamado,
            "Prom. Atención": item.promedioAtencion
        }));
        const wsAtenciones = XLSX.utils.json_to_sheet(atencionesData);
        XLSX.utils.book_append_sheet(wb, wsAtenciones, "Por Tipo de Atención");

        // 4. Por Módulo
        const modulosData = (dashboard.porModulo || []).map(item => ({
            "Módulo / Consultorio": item.modulo,
            "Cantidad Atendidos": item.cantidad,
            "Prom. Llamado": item.promedioLlamado,
            "Prom. Atención": item.promedioAtencion
        }));
        const wsModulos = XLSX.utils.json_to_sheet(modulosData);
        XLSX.utils.book_append_sheet(wb, wsModulos, "Por Módulo");

        // 5. Por Agente
        const agentesData = (dashboard.porAgente || []).map(item => ({
            "Agente": item.agente,
            "Cantidad Atendidos": item.cantidad,
            "Prom. Llamado": item.promedioLlamado,
            "Prom. Atención": item.promedioAtencion
        }));
        const wsAgentes = XLSX.utils.json_to_sheet(agentesData);
        XLSX.utils.book_append_sheet(wb, wsAgentes, "Por Agente");

        // 6. Evolución
        const evolucionData = (dashboard.evolucion || []).map(item => ({
            "Fecha": item.fecha,
            "Creados": item.creados,
            "Atendidos": item.atendidos
        }));
        const wsEvolucion = XLSX.utils.json_to_sheet(evolucionData);
        XLSX.utils.book_append_sheet(wb, wsEvolucion, "Evolución Temporal");

        XLSX.writeFile(wb, `Reporte_Dashboard_${query.fechaInicio}_al_${query.fechaFinal}.xlsx`);
    };

    // Configuración de la tabla de especialistas (Agentes y Médicos)
    const filteredEspecialistas = (dashboard.especialistas || []).filter(esp => {
        const matchesSearch = (esp.nombreCompleto || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = roleFilter === 'ALL' || esp.tipo === roleFilter;
        return matchesSearch && matchesRole;
    });

    const maxAtenciones = filteredEspecialistas.length > 0
        ? Math.max(...filteredEspecialistas.map(e => e.cantidadAtendidos))
        : 1;

    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'nombreCompleto',
            key: 'nombreCompleto',
            render: (text) => <span style={{ fontWeight: 600, color: '#334155' }}>{text}</span>,
            sorter: (a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto)
        },
        {
            title: 'Rol',
            dataIndex: 'tipo',
            key: 'tipo',
            align: 'center',
            sorter: (a, b) => a.tipo.localeCompare(b.tipo),
            render: (tipo) => (
                <span
                    className="badge"
                    style={{
                        fontSize: 11,
                        padding: '4px 8px',
                        borderRadius: 6,
                        fontWeight: 600,
                        backgroundColor: tipo === 'MEDICO' ? '#ecfdf5' : '#eff6ff',
                        color: tipo === 'MEDICO' ? '#059669' : '#2563eb',
                        border: `1px solid ${tipo === 'MEDICO' ? '#a7f3d0' : '#bfdbfe'}`
                    }}
                >
                    {tipo === 'MEDICO' ? 'Médico' : 'Agente'}
                </span>
            )
        },
        {
            title: 'Pacientes Atendidos',
            dataIndex: 'cantidadAtendidos',
            key: 'cantidadAtendidos',
            sorter: (a, b) => a.cantidadAtendidos - b.cantidadAtendidos,
            render: (val) => {
                const percent = Math.round((val / maxAtenciones) * 100);
                return (
                    <div style={{ minWidth: 150 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontWeight: 'bold' }}>{val}</span>
                            <span style={{ color: '#94a3b8', fontSize: 11 }}>{percent}%</span>
                        </div>
                        <Progress percent={percent} showInfo={false} strokeColor="#06b6d4" size="small" />
                    </div>
                );
            }
        },
        {
            title: 'T. Promedio Llamado',
            dataIndex: 'promedioLlamado',
            key: 'promedioLlamado',
            render: (val) => (
                <span>
                    <i className="fa-regular fa-clock" style={{ marginRight: 6, color: '#94a3b8' }}></i>
                    {val}
                </span>
            )
        },
        {
            title: 'T. Espera / Inicio',
            dataIndex: 'promedioEsperaAtencion',
            key: 'promedioEsperaAtencion',
            render: (val) => (
                <span>
                    <i className="fa-solid fa-arrows-rotate fa-spin" style={{ marginRight: 6, color: '#f59e0b' }}></i>
                    {val}
                </span>
            )
        },
        {
            title: 'T. Atención (Consulta)',
            dataIndex: 'promedioAtencion',
            key: 'promedioAtencion',
            render: (val) => (
                <span>
                    <i className="fa-regular fa-circle-check" style={{ marginRight: 6, color: '#10b981' }}></i>
                    {val}
                </span>
            )
        },
        {
            title: 'Llamados / Ticket',
            dataIndex: 'promedioLlamadosNum',
            key: 'promedioLlamadosNum',
            align: 'center',
            render: (val) => <span style={{ fontWeight: 500 }}>{val}</span>
        },
        {
            title: 'Sin Responder',
            dataIndex: 'totalNoRespondio',
            key: 'totalNoRespondio',
            align: 'center',
            render: (val) => (
                <span className={`badge ${val > 0 ? 'bg-danger-subtle text-danger' : 'bg-light text-muted'}`} style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6 }}>
                    {val}
                </span>
            )
        },
        {
            title: 'Cancelados',
            dataIndex: 'totalCancelado',
            key: 'totalCancelado',
            align: 'center',
            render: (val) => (
                <span className={`badge ${val > 0 ? 'bg-warning-subtle text-warning' : 'bg-light text-muted'}`} style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6 }}>
                    {val}
                </span>
            )
        }
    ];

    if (usuario.uidUsuario === null) return <></>;
    if (!pantallas.includes('informes')) return <NoAcceso />;

    const pieData = (dashboard.estados || []).map(item => ({
        name: item.estado,
        value: item.cantidad
    }));

    const opcionesTipos = dashboard.opciones?.tiposAtencion || [];
    const opcionesModulos = dashboard.opciones?.modulos || [];
    const opcionesAgentes = dashboard.opciones?.agentes || [];

    return (
        <div className={`p-4 ${isLoading ? 'opacity-75' : ''}`} style={{ minHeight: '100vh', background: '#f8fafc' }}>
            {isLoading && <LoadingOverlay />}

            {/* Cabecera del Dashboard */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                    <h2 style={{ fontWeight: 800, color: '#0f172a', margin: 0 }}>
                        Dashboard
                    </h2>
                    <p className="text-muted" style={{ margin: 0 }}>
                        Monitoreo de atención clínica, tiempos promedio de espera y rendimiento.
                    </p>
                </div>

                <Button
                    type="primary"
                    icon={<i className="fa-regular fa-file-excel"></i>}
                    size="large"
                    onClick={exportToExcelMultiSheet}
                    style={{
                        backgroundColor: '#10b981',
                        borderColor: '#10b981',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                        fontWeight: 600
                    }}
                >
                    Exportar Reporte Completo a Excel
                </Button>
            </div>

            {/* Panel de Filtros */}
            <Card
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: '16px', background: '#ffffff' }}
            >
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold mb-0 text-uppercase" style={{ letterSpacing: 0.5, color: '#1e293b' }}>
                        Filtros
                    </h6>
                    <Button
                        icon={<i className="fa-solid fa-arrows-rotate me-1"></i>}
                        onClick={cargarDashboard}
                        style={{ borderRadius: '8px', height: 34 }}
                    >
                        Actualizar
                    </Button>
                </div>

                {/* Botones Presets Año / Mes / Día */}
                <div className="d-flex gap-2 mb-3">
                    <button
                        onClick={handlePresetAnio}
                        className={`btn btn-sm ${query.filtroTiempo === 'anio' ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-3 fw-semibold`}
                        style={{ fontSize: 13 }}
                    >
                        Año
                    </button>
                    <button
                        onClick={handlePresetMes}
                        className={`btn btn-sm ${query.filtroTiempo === 'mes' ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-3 fw-semibold`}
                        style={{ fontSize: 13 }}
                    >
                        Mes
                    </button>
                    <button
                        onClick={handlePresetDia}
                        className={`btn btn-sm ${query.filtroTiempo === 'dia' ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-3 fw-semibold`}
                        style={{ fontSize: 13 }}
                    >
                        Día
                    </button>
                </div>

                {/* Inputs de filtro */}
                <div className="row g-3 align-items-end">
                    {/* Inicio */}
                    <div className="col-12 col-sm-6 col-md-4 col-xl">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: 12 }}>Inicio</label>
                        <input
                            type="date"
                            name="fechaInicio"
                            className="form-control rounded-3 border-secondary-subtle"
                            value={query.fechaInicio}
                            onChange={handleFilterChange}
                            style={{ fontSize: 13, height: 40 }}
                        />
                    </div>

                    {/* Final */}
                    <div className="col-12 col-sm-6 col-md-4 col-xl">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: 12 }}>Final</label>
                        <input
                            type="date"
                            name="fechaFinal"
                            className="form-control rounded-3 border-secondary-subtle"
                            value={query.fechaFinal}
                            min={query.fechaInicio}
                            onChange={handleFilterChange}
                            style={{ fontSize: 13, height: 40 }}
                        />
                    </div>

                    {/* Tipos Atención */}
                    <div className="col-12 col-sm-6 col-md-4 col-xl">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: 12 }}>Tipos Atención</label>
                        <select
                            name="uidTipoAtencion"
                            className="form-select rounded-3 border-secondary-subtle"
                            value={query.uidTipoAtencion}
                            onChange={handleFilterChange}
                            style={{ fontSize: 13, height: 40 }}
                        >
                            <option value="">Seleccione</option>
                            {opcionesTipos.map(item => (
                                <option key={item.uidTipoAtencion} value={item.uidTipoAtencion}>
                                    {item.tipoAtencion}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Módulos */}
                    <div className="col-12 col-sm-6 col-md-4 col-xl">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: 12 }}>Módulos</label>
                        <select
                            name="uidModulo"
                            className="form-select rounded-3 border-secondary-subtle"
                            value={query.uidModulo}
                            onChange={handleFilterChange}
                            style={{ fontSize: 13, height: 40 }}
                        >
                            <option value="">Seleccione</option>
                            {opcionesModulos.map(item => (
                                <option key={item.uidModulo} value={item.uidModulo}>
                                    {item.modulo}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Agentes */}
                    <div className="col-12 col-sm-6 col-md-4 col-xl">
                        <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: 12 }}>Agentes</label>
                        <select
                            name="uidAgente"
                            className="form-select rounded-3 border-secondary-subtle"
                            value={query.uidAgente}
                            onChange={handleFilterChange}
                            style={{ fontSize: 13, height: 40 }}
                        >
                            <option value="">Seleccione</option>
                            {opcionesAgentes.map(item => (
                                <option key={item.uidUsuario} value={item.uidUsuario}>
                                    {item.nombreCompleto}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </Card>

            {/* SECCIÓN NUEVA DE LAS 3 TABLAS RESUMEN */}
            <TablasResumenDashboard dashboard={dashboard} query={query} />

            {/* KPI Cards Grid Originales */}
            <div className="row g-3 mb-4">
                {/* Card 1: Creados */}
                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="card border-0 p-3 h-100 position-relative overflow-hidden"
                        style={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                            color: '#ffffff',
                            boxShadow: '0 8px 20px rgba(79, 70, 229, 0.15)'
                        }}
                    >
                        <div className="position-absolute" style={{ right: -15, bottom: -15, opacity: 0.15, fontSize: 100 }}>
                            <i className="fa-solid fa-ticket"></i>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.8 }}>TICKETS CREADOS</span>
                        <h2 className="mt-2 mb-1" style={{ fontSize: 32, fontWeight: 800 }}>
                            {dashboard.resumen.creados}
                        </h2>
                        <small style={{ opacity: 0.75 }}>En el periodo seleccionado</small>
                    </div>
                </div>

                {/* Card 2: Atendidos */}
                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="card border-0 p-3 h-100 position-relative overflow-hidden"
                        style={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: '#ffffff',
                            boxShadow: '0 8px 20px rgba(16, 185, 129, 0.15)'
                        }}
                    >
                        <div className="position-absolute" style={{ right: -15, bottom: -15, opacity: 0.15, fontSize: 100 }}>
                            <i className="fa-solid fa-circle-check"></i>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.8 }}>TICKETS ATENDIDOS</span>
                        <h2 className="mt-2 mb-1" style={{ fontSize: 32, fontWeight: 800 }}>
                            {dashboard.resumen.atendidos}
                        </h2>
                        <small style={{ opacity: 0.75 }}>Total atención (sede)</small>
                    </div>
                </div>

                {/* Card 3: Consultorios */}
                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="card border-0 p-3 h-100 position-relative overflow-hidden"
                        style={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, #06b6d4, #0284c7)',
                            color: '#ffffff',
                            boxShadow: '0 8px 20px rgba(6, 182, 212, 0.15)'
                        }}
                    >
                        <div className="position-absolute" style={{ right: -15, bottom: -15, opacity: 0.15, fontSize: 100 }}>
                            <i className="fa-solid fa-user-doctor"></i>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.8 }}>ATENCIONES CONSULTORIO</span>
                        <h2 className="mt-2 mb-1" style={{ fontSize: 32, fontWeight: 800 }}>
                            {dashboard.resumen.atendidosConsultorio}
                        </h2>
                        <small style={{ opacity: 0.75 }}>Atendidos por especialistas</small>
                    </div>
                </div>

                {/* Card 4: % Consultorios */}
                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="card border-0 p-3 h-100 position-relative overflow-hidden"
                        style={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, #f43f5e, #db2777)',
                            color: '#ffffff',
                            boxShadow: '0 8px 20px rgba(244, 63, 94, 0.15)'
                        }}
                    >
                        <div className="position-absolute" style={{ right: -15, bottom: -15, opacity: 0.15, fontSize: 100 }}>
                            <i className="fa-solid fa-chart-pie"></i>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.8 }}>PARTICIPACIÓN CONSULTORIO</span>
                        <h2 className="mt-2 mb-1" style={{ fontSize: 32, fontWeight: 800 }}>
                            {dashboard.resumen.atendidos
                                ? `${((dashboard.resumen.atendidosConsultorio / dashboard.resumen.atendidos) * 100).toFixed(1)}%`
                                : '0.0%'
                            }
                        </h2>
                        <small style={{ opacity: 0.75 }}>Porcentaje de atención en consultorios</small>
                    </div>
                </div>
            </div>

            {/* KPI Times Grid Originales */}
            <div className="row g-3 mb-4">
                {/* Card 5: Promedio Llamado */}
                <div className="col-12 col-sm-6 col-lg-4">
                    <div className="card border-0 p-3 h-100 position-relative overflow-hidden"
                        style={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                            color: '#ffffff',
                            boxShadow: '0 8px 20px rgba(245, 158, 11, 0.15)'
                        }}
                    >
                        <div className="position-absolute" style={{ right: -15, bottom: -15, opacity: 0.15, fontSize: 100 }}>
                            <i className="fa-regular fa-clock"></i>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.8 }}>T. PROMEDIO DE LLAMADO</span>
                        <h2 className="mt-2 mb-1" style={{ fontSize: 32, fontWeight: 800 }}>
                            {dashboard.resumen.promedioLlamado || '00:00:00'}
                        </h2>
                        <small style={{ opacity: 0.75 }}>Espera en cola antes de ser llamado</small>
                    </div>
                </div>

                {/* Card 6: Promedio Espera Atención */}
                <div className="col-12 col-sm-6 col-lg-4">
                    <div className="card border-0 p-3 h-100 position-relative overflow-hidden"
                        style={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                            color: '#ffffff',
                            boxShadow: '0 8px 20px rgba(139, 92, 246, 0.15)'
                        }}
                    >
                        <div className="position-absolute" style={{ right: -15, bottom: -15, opacity: 0.15, fontSize: 100 }}>
                            <i className="fa-solid fa-arrows-rotate"></i>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.8 }}>T. PROMEDIO EN INICIAR ATENCIÓN</span>
                        <h2 className="mt-2 mb-1" style={{ fontSize: 32, fontWeight: 800 }}>
                            {dashboard.resumen.promedioEsperaAtencion || '00:00:00'}
                        </h2>
                        <small style={{ opacity: 0.75 }}>Espera en recepción / traslado</small>
                    </div>
                </div>

                {/* Card 7: Promedio Atención */}
                <div className="col-12 col-sm-6 col-lg-4">
                    <div className="card border-0 p-3 h-100 position-relative overflow-hidden"
                        style={{
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, #10b981, #047857)',
                            color: '#ffffff',
                            boxShadow: '0 8px 20px rgba(16, 185, 129, 0.15)'
                        }}
                    >
                        <div className="position-absolute" style={{ right: -15, bottom: -15, opacity: 0.15, fontSize: 100 }}>
                            <i className="fa-regular fa-circle-check"></i>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.8 }}>T. PROMEDIO DE ATENCIÓN</span>
                        <h2 className="mt-2 mb-1" style={{ fontSize: 32, fontWeight: 800 }}>
                            {dashboard.resumen.promedioAtencion || '00:00:00'}
                        </h2>
                        <small style={{ opacity: 0.75 }}>Duración de la consulta o servicio</small>
                    </div>
                </div>
            </div>

            {/* Gráficos de Primera Fila Originales */}
            <div className="row g-4 mb-4">
                {/* Gráfico de Evolución Temporal */}
                <div className="col-12 col-lg-8">
                    <Card
                        title={<span style={{ fontWeight: 700, color: '#1e293b' }}>Evolución Temporal de Consultorios</span>}
                        className="border-0 shadow-sm h-100"
                        style={{ borderRadius: '16px' }}
                    >
                        {(dashboard.evolucion || []).length === 0 ? (
                            <div className="text-center py-5 text-muted">No hay datos para el rango de fechas seleccionado</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={320}>
                                <AreaChart data={dashboard.evolucion} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorCreados" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="colorAtendidos" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="fecha" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                                    <Legend verticalAlign="top" height={36} iconType="circle" />
                                    <Area name="Tickets Creados" type="monotone" dataKey="creados" stroke="#6366f1" strokeWidth={2} fillOpacity={1} fill="url(#colorCreados)" />
                                    <Area name="Pacientes Atendidos" type="monotone" dataKey="atendidos" stroke="#06b6d4" strokeWidth={2} fillOpacity={1} fill="url(#colorAtendidos)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </div>

                {/* Gráfico de Tasa de Respuestas y Cancelaciones */}
                <div className="col-12 col-lg-4">
                    <Card
                        title={<span style={{ fontWeight: 700, color: '#1e293b' }}>Distribución de Estados</span>}
                        className="border-0 shadow-sm h-100"
                        style={{ borderRadius: '16px' }}
                    >
                        {(dashboard.estados || []).length === 0 ? (
                            <div className="text-center py-5 text-muted">Sin datos de flujo</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={320}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={65}
                                        outerRadius={90}
                                        paddingAngle={4}
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={ESTADOS_COLORS[entry.name] || COLORS_SERIES[index % COLORS_SERIES.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => [`${value} flujos`, 'Cantidad']} contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                                    <Legend iconType="circle" layout="vertical" align="right" verticalAlign="middle" />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </div>
            </div>

            {/* Gráficos de Segunda Fila Originales: Modulos y Atenciones */}
            <div className="row g-4 mb-4">
                {/* Tickets por Módulo */}
                <div className="col-12 col-md-6">
                    <Card
                        title={<span style={{ fontWeight: 700, color: '#1e293b' }}>Atenciones por Módulo / Consultorio</span>}
                        className="border-0 shadow-sm"
                        style={{ borderRadius: '16px' }}
                    >
                        {(dashboard.porModulo || []).length === 0 ? (
                            <div className="text-center py-5 text-muted">No hay datos registrados</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={dashboard.porModulo} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis dataKey="modulo" type="category" tick={{ fill: '#475569', fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} width={80} />
                                    <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                                    <Bar dataKey="cantidad" name="Atenciones" radius={[0, 8, 8, 0]} maxBarSize={30}>
                                        {(dashboard.porModulo || []).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS_SERIES[index % COLORS_SERIES.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </div>

                {/* Tickets por Tipo de Atención */}
                <div className="col-12 col-md-6">
                    <Card
                        title={<span style={{ fontWeight: 700, color: '#1e293b' }}>Atenciones por Tipo de Atención</span>}
                        className="border-0 shadow-sm"
                        style={{ borderRadius: '16px' }}
                    >
                        {(dashboard.porAtencion || []).length === 0 ? (
                            <div className="text-center py-5 text-muted">No hay datos registrados</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={dashboard.porAtencion} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis dataKey="tipoAtencion" type="category" tick={{ fill: '#475569', fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} width={90} />
                                    <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                                    <Bar dataKey="cantidad" name="Atenciones" radius={[0, 8, 8, 0]} maxBarSize={30}>
                                        {(dashboard.porAtencion || []).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS_SERIES[(index + 3) % COLORS_SERIES.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </div>
            </div>

            {/* Tabla Detallada Original de Especialistas */}
            <Card
                title={
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 py-2">
                        <div>
                            <span style={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>Rendimiento y Tiempos de Personal</span>
                            <span className="text-muted d-block" style={{ fontSize: 12, fontWeight: 400, marginTop: 2 }}>Métricas detalladas por agentes de recepción y médicos.</span>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Radio.Group
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                                buttonStyle="solid"
                            >
                                <Radio.Button value="ALL">Todos</Radio.Button>
                                <Radio.Button value="AGENTE">Agentes</Radio.Button>
                                <Radio.Button value="MEDICO">Médicos</Radio.Button>
                            </Radio.Group>
                            <Input
                                placeholder="Buscar por nombre..."
                                prefix={<i className="fa-solid fa-magnifying-glass" style={{ color: '#cbd5e1' }}></i>}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ width: 200, borderRadius: '8px' }}
                                allowClear
                            />
                        </div>
                    </div>
                }
                className="border-0 shadow-sm"
                style={{ borderRadius: '16px', overflow: 'hidden' }}
            >
                <Table
                    columns={columns}
                    dataSource={filteredEspecialistas}
                    rowKey="nombreCompleto"
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                />
            </Card>

        </div>
    );
};
